// src/utils/dietaryUtils.ts

export interface DietaryInfo {
  isVegan: boolean;
  isVegetarian: boolean;
  isNonVegetarian: boolean;
  isGlutenFree?: boolean;
}

export type DietaryType = 'vegan' | 'vegetarian' | 'non-vegetarian';

/**
 * Determines the dietary status of a menu item based on priority system
 * Priority: is_vegan field → veg field → food_type fallback
 * Rules:
 * 1. is_vegan = "yes" → VEGAN (regardless of other fields)
 * 2. is_vegan = "no" AND veg = "yes" → VEGETARIAN
 * 3. is_vegan = "no" AND veg = "no" → NON-VEGETARIAN
 * 4. If veg field missing, use food_type as fallback
 */
export const getDietaryInfo = (item: any): DietaryInfo => {
  // Initialize default values
  let isVegan = false;
  let isVegetarian = false;
  let isNonVegetarian = false;
  let isGlutenFree = false;

  // Helper function to convert any value to string and normalize
  const normalizeValue = (value: any): string => {
    if (value == null) return '';
    return value.toString().toLowerCase().trim();
  };

  // Step 1: Check is_vegan field first (highest priority)
  const isVeganValue = normalizeValue(item.is_vegan);
  const vegValue = normalizeValue(item.veg);
  const foodTypeValue = normalizeValue(item.food_type);

  // Check dietary.isVegan as additional source for is_vegan
  let effectiveIsVegan = isVeganValue;
  if (!effectiveIsVegan && item.dietary?.isVegan === true) {
    effectiveIsVegan = 'yes';
  } else if (!effectiveIsVegan && item.dietary?.isVegan === false) {
    effectiveIsVegan = 'no';
  }

  // Apply priority logic
  if (effectiveIsVegan === 'yes' || effectiveIsVegan === 'true' || effectiveIsVegan === '1') {
    // Rule 1: is_vegan = "yes" → VEGAN
    isVegan = true;
    isVegetarian = true;
    isNonVegetarian = false;
  } else if (effectiveIsVegan === 'no' || effectiveIsVegan === 'false' || effectiveIsVegan === '0') {
    // is_vegan = "no", now check veg field
    if (vegValue === 'yes' || vegValue === 'true' || vegValue === '1') {
      // Rule 2: is_vegan = "no" AND veg = "yes" → VEGETARIAN
      isVegan = false;
      isVegetarian = true;
      isNonVegetarian = false;
    } else if (vegValue === 'no' || vegValue === 'false' || vegValue === '0') {
      // Rule 3: is_vegan = "no" AND veg = "no" → NON-VEGETARIAN
      isVegan = false;
      isVegetarian = false;
      isNonVegetarian = true;
    } else {
      // veg field is missing, use food_type as fallback
      if (foodTypeValue.includes('non_veg') || foodTypeValue.includes('non-veg') || 
          foodTypeValue.includes('nonveg') || foodTypeValue.includes('meat') || 
          foodTypeValue.includes('chicken') || foodTypeValue.includes('beef') || 
          foodTypeValue.includes('pork') || foodTypeValue.includes('fish') || 
          foodTypeValue.includes('seafood') || foodTypeValue.includes('lamb') ||
          foodTypeValue.includes('turkey') || foodTypeValue.includes('duck')) {
        // food_type indicates non-vegetarian
        isVegan = false;
        isVegetarian = false;
        isNonVegetarian = true;
      } else if (foodTypeValue.includes('veg') || foodTypeValue.includes('vegetarian')) {
        // food_type indicates vegetarian
        isVegan = false;
        isVegetarian = true;
        isNonVegetarian = false;
      } else {
        // No clear indication, default to non-vegetarian for safety
        isVegan = false;
        isVegetarian = false;
        isNonVegetarian = true;
      }
    }
  } else {
    // is_vegan field is missing or unclear, use food_type as primary fallback
    if (foodTypeValue.includes('vegan')) {
      isVegan = true;
      isVegetarian = true;
      isNonVegetarian = false;
    } else if (foodTypeValue.includes('non_veg') || foodTypeValue.includes('non-veg') || 
               foodTypeValue.includes('nonveg') || foodTypeValue.includes('meat') || 
               foodTypeValue.includes('chicken') || foodTypeValue.includes('beef') || 
               foodTypeValue.includes('pork') || foodTypeValue.includes('fish') || 
               foodTypeValue.includes('seafood') || foodTypeValue.includes('lamb') ||
               foodTypeValue.includes('turkey') || foodTypeValue.includes('duck')) {
      isVegan = false;
      isVegetarian = false;
      isNonVegetarian = true;
    } else if (foodTypeValue.includes('veg') || foodTypeValue.includes('vegetarian')) {
      isVegan = false;
      isVegetarian = true;
      isNonVegetarian = false;
    } else {
      // Final fallback: check item name/description for non-veg keywords
      const itemText = `${item.name || ''} ${item.description || ''}`.toLowerCase();
      const nonVegKeywords = ['chicken', 'beef', 'pork', 'lamb', 'fish', 'shrimp', 'crab', 'lobster', 'meat', 'bacon', 'ham', 'turkey', 'duck', 'wings'];
      const hasNonVegKeywords = nonVegKeywords.some(keyword => itemText.includes(keyword));
      
      if (hasNonVegKeywords) {
        isVegan = false;
        isVegetarian = false;
        isNonVegetarian = true;
      } else {
        // Default to non-vegetarian for safety when unclear
        isVegan = false;
        isVegetarian = false;
        isNonVegetarian = true;
      }
    }
  }

  // Check for gluten-free indicators
  if (item.dietary?.isGlutenFree) {
    isGlutenFree = true;
  }

  return {
    isVegan,
    isVegetarian,
    isNonVegetarian,
    isGlutenFree
  };
};

/**
 * Gets the primary dietary type for display purposes
 */
export const getPrimaryDietaryType = (item: any): DietaryType => {
  const dietaryInfo = getDietaryInfo(item);
  
  if (dietaryInfo.isVegan) return 'vegan';
  if (dietaryInfo.isVegetarian) return 'vegetarian';
  return 'non-vegetarian';
};

/**
 * Gets the appropriate color for dietary type
 */
export const getDietaryColor = (dietaryType: DietaryType): string => {
  switch (dietaryType) {
    case 'vegan':
      return 'text-green-600 border-green-600';
    case 'vegetarian':
      return 'text-green-600 border-green-600';
    case 'non-vegetarian':
      return 'text-red-600 border-red-600';
    default:
      return 'text-gray-600 border-gray-600';
  }
};

/**
 * Gets the appropriate background color for dietary type
 */
export const getDietaryBgColor = (dietaryType: DietaryType): string => {
  switch (dietaryType) {
    case 'vegan':
      return 'bg-green-500';
    case 'vegetarian':
      return 'bg-green-500';
    case 'non-vegetarian':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};
